import React from 'react'
import styled, { keyframes } from 'styled-components'

interface AppVersionPromptProps {
    remoteVersion: string
    currentVersion: string
    onUpdate: () => void
    onSkip: () => void
}

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(0.6em); }
    to   { opacity: 1; transform: translateY(0); }
`

const Wrapper = styled.div`
    position: fixed;
    bottom: 1.25em;
    left: 1.25em;
    z-index: 50;
    display: grid;
    grid-template-rows: auto auto;
    row-gap: 0.5em;
    padding: 0.75em 1em;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(12px) saturate(160%);
    -webkit-backdrop-filter: blur(12px) saturate(160%);
    border: 1px solid rgba(41, 127, 118, 0.18);
    box-shadow: 0 12px 32px -8px rgba(41, 127, 118, 0.25), 0 2px 4px rgba(0, 0, 0, 0.04);
    direction: rtl;
    color: #2f3a3a;
    animation: ${slideUp} 0.25s ease-out;
    min-width: 16em;
`

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.55em;
    font-size: 0.9em;
`

const Pulse = styled.span`
    flex: 0 0 auto;
    width: 0.55em;
    height: 0.55em;
    border-radius: 50%;
    background: #36c39b;
    box-shadow: 0 0 0 0 rgba(54, 195, 155, 0.6);
    animation: ${keyframes`
        0% { box-shadow: 0 0 0 0 rgba(54, 195, 155, 0.55); }
        70% { box-shadow: 0 0 0 0.55em rgba(54, 195, 155, 0); }
        100% { box-shadow: 0 0 0 0 rgba(54, 195, 155, 0); }
    `} 1.8s ease-out infinite;
`

const Label = styled.span`
    font-weight: 600;
    color: #297f76;
`

const VersionDelta = styled.span`
    direction: ltr;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8em;
    color: #6b7a7a;
    background: rgba(41, 127, 118, 0.08);
    padding: 0.15em 0.55em;
    border-radius: 999px;
    white-space: nowrap;
`

const ActionsRow = styled.div`
    display: flex;
    gap: 0.4em;
    justify-content: flex-start;
`

const PrimaryButton = styled.button`
    background: linear-gradient(135deg, #2f8f86 0%, #226b63 100%);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    padding: 0.4em 1.1em;
    font-size: 0.85em;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.15s ease;
    box-shadow: 0 2px 6px rgba(41, 127, 118, 0.35);
    &:hover  { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(41, 127, 118, 0.45); }
    &:active { transform: translateY(0); }
`

const SecondaryButton = styled.button`
    background: transparent;
    color: #6b7a7a;
    border: none;
    border-radius: 10px;
    padding: 0.4em 0.9em;
    font-size: 0.85em;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    &:hover { background: rgba(0, 0, 0, 0.05); color: #2f3a3a; }
`

const AppVersionPrompt: React.FC<AppVersionPromptProps> = ({
    remoteVersion, currentVersion, onUpdate, onSkip,
}) => (
    <Wrapper role="dialog" aria-live="polite">
        <InfoRow>
            <Pulse />
            <Label>גרסה חדשה זמינה</Label>
            <VersionDelta>{currentVersion} → {remoteVersion}</VersionDelta>
        </InfoRow>
        <ActionsRow>
            <PrimaryButton onClick={onUpdate} type="button">עדכן</PrimaryButton>
            <SecondaryButton onClick={onSkip} type="button">הסתר</SecondaryButton>
        </ActionsRow>
    </Wrapper>
)

AppVersionPrompt.displayName = 'AppVersionPrompt'

export default AppVersionPrompt
